import { useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

import { Content } from './content'
import { useProcessingWarning } from '../hooks/use-processing-warning'
import { WarningPopup } from '../components/warning-popup'
import { useCSVUpload } from '../hooks/use-csv-upload'
import * as Styled from './styled'
import { VideosProvider } from '../context/videos-context'

interface IModalProps {
  isOpen: boolean
  onClose: () => void
}

const ModalContent = ({ isOpen, onClose }: IModalProps) => {
  const {
    showWarning,
    requestClose,
    handleCancelWarning,
    handleConfirmWarning
  } = useProcessingWarning()

  const {
    fileInputRef,
    handleFileSelect,
    triggerFileInput,
    error,
    isUploading
  } = useCSVUpload()

  const handleCloseRequest = useCallback(() => {
    requestClose(onClose)
  }, [requestClose, onClose])

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseRequest()
      }
    }

    const handleBodyScroll = () => {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }

    document.addEventListener('keydown', handleEscape)
    const cleanupScroll = handleBodyScroll()

    return () => {
      document.removeEventListener('keydown', handleEscape)
      cleanupScroll()
    }
  }, [isOpen, handleCloseRequest])

  if (!isOpen) return null

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      handleCloseRequest()
    }
  }

  return createPortal(
    <>
      <Styled.Overlay
        className="prompt-to-video-modal"
        onClick={handleOverlayClick}
      >
        <Styled.ModalContainer>
          <Styled.ModalHeader>
            <Styled.ModalTitle>Prompt to Video Playground</Styled.ModalTitle>
            <Styled.HeaderActions>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Styled.UploadButton
                onClick={triggerFileInput}
                disabled={isUploading}
              >
                📄 {isUploading ? 'Uploading...' : 'Upload CSV'}
              </Styled.UploadButton>
              <Styled.CloseButton
                onClick={handleCloseRequest}
                aria-label="Close modal"
              >
                ×
              </Styled.CloseButton>
            </Styled.HeaderActions>
          </Styled.ModalHeader>
          <Styled.ModalContent>
            <Content />
          </Styled.ModalContent>
        </Styled.ModalContainer>
      </Styled.Overlay>
      {error && (
        <Styled.UploadMessage
          className="prompt-to-video-modal-upload-message"
          $isError={!error.warnings}
        >
          <Styled.UploadMessageText $isError={!error.warnings}>
            {error.message}
          </Styled.UploadMessageText>
          {error.warnings && error.warnings.length > 0 && (
            <Styled.WarningsList>
              {error.warnings.slice(0, 5).map(warning => (
                <Styled.WarningItem key={warning}>{warning}</Styled.WarningItem>
              ))}
              {error.warnings.length > 5 && (
                <Styled.WarningItem>
                  ... and {error.warnings.length - 5} more warning(s)
                </Styled.WarningItem>
              )}
            </Styled.WarningsList>
          )}
        </Styled.UploadMessage>
      )}
      <WarningPopup
        isOpen={showWarning}
        onCancel={handleCancelWarning}
        onConfirm={handleConfirmWarning}
      />
    </>,
    document.body
  )
}

export const Modal = ({ isOpen, onClose }: IModalProps) => {
  return (
    <VideosProvider>
      <ModalContent isOpen={isOpen} onClose={onClose} />
    </VideosProvider>
  )
}
